/**
 * Cache das varreduras: o MESMO cache em dois níveis do satélite
 * (`goes/frameCache.ts`) — blobs PNG baixados de todas as varreduras do loop
 * e só uma janela decodificada —, numa instância própria, com um carregador
 * que REPROJETA ao decodificar (ver `geometria.ts` e o worker).
 *
 * Instância própria porque os orçamentos são outros: um PNG de radar tem
 * 4–40 KB (um quadro do GOES em 3600 px, 2,8 MB), e são 9 radares por quadro.
 */
import { FrameCache, type FrameLoader } from '../goes/frameCache';
import { fetchWithRetry } from '../lib/fetchRetry';
import { radarDaChave } from './api';
import type { GeometriaRadar, ModeloOrigem } from './geometria';
import { isProduto, trocaDeCores } from './produtos';
import { reprojetarBlob, type PedidoReprojecao, type RespostaReprojecao } from './reprojetar.worker';

/** Geometria de cada radar (id), calculada quando o manifesto chega. */
const geometrias = new Map<string, { chave: string; geo: GeometriaRadar; modelo: ModeloOrigem }>();

export function definirGeometria(radar: string, chave: string, geo: GeometriaRadar, modelo: ModeloOrigem): void {
  geometrias.set(radar, { chave, geo, modelo });
}

export function geometriaDe(radar: string): GeometriaRadar | null {
  return geometrias.get(radar)?.geo ?? null;
}

/** Onde cada lon/lat cai no PNG do radar (para ler o pixel no clique). */
export function modeloDe(radar: string): ModeloOrigem | null {
  return geometrias.get(radar)?.modelo ?? null;
}

// ---------------------------------------------------------------------------
// Workers de reprojeção
// ---------------------------------------------------------------------------

/**
 * Dois workers: reprojetar ~1 Mpx custa ~10 ms, e são 9 radares por quadro.
 * Com dois, a janela à frente da animação se enche mais rápido do que ela anda.
 */
const WORKERS = 2;

interface Pool {
  workers: Worker[];
  pendentes: Map<number, { resolve: (img: ImageBitmap) => void; reject: (e: unknown) => void }>;
  seq: number;
}

let pool: Pool | null | undefined;

function obterPool(): Pool | null {
  if (pool !== undefined) return pool;
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return (pool = null);
  try {
    const p: Pool = { workers: [], pendentes: new Map(), seq: 0 };
    for (let i = 0; i < WORKERS; i++) {
      const w = new Worker(new URL('./reprojetar.worker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (e: MessageEvent<RespostaReprojecao>) => {
        const req = p.pendentes.get(e.data.id);
        if (!req) return;
        p.pendentes.delete(e.data.id);
        if (e.data.ok) req.resolve(e.data.image);
        else req.reject(new Error(e.data.error));
      };
      w.onerror = () => {
        // Worker indisponível (ex.: versão nova publicada): quem espera recebe
        // erro e a reprojeção passa para a thread principal.
        for (const req of p.pendentes.values()) req.reject(new Error('worker de reprojeção indisponível'));
        p.pendentes.clear();
        for (const x of p.workers) x.terminate();
        pool = null;
      };
      p.workers.push(w);
    }
    return (pool = p);
  } catch {
    return (pool = null);
  }
}

/**
 * Encerra os workers (e as tabelas de reprojeção que eles guardam, ~4 MB por
 * radar) quando a camada é desligada. Religar cria de novo.
 */
export function encerrarWorkers(): void {
  if (!pool) return;
  for (const req of pool.pendentes.values()) req.reject(new Error('camada de radar desligada'));
  for (const w of pool.workers) w.terminate();
  pool = undefined;
}

/** Worker fixo por radar: cada tabela de reprojeção vive num worker só (e não em todos). */
function workerDo(p: Pool, chave: string): Worker {
  let h = 0;
  for (let i = 0; i < chave.length; i++) h = (h * 31 + chave.charCodeAt(i)) | 0;
  return p.workers[(h >>> 0) % p.workers.length];
}

function noWorker(pedido: Omit<PedidoReprojecao, 'id'>): Promise<ImageBitmap> | null {
  const p = obterPool();
  if (!p) return null;
  const id = ++p.seq;
  const w = workerDo(p, pedido.chave.split('|')[0]);
  return new Promise<ImageBitmap>((resolve, reject) => {
    p.pendentes.set(id, { resolve, reject });
    // A grade vai por cópia (≈ 30 KB): o worker guarda a tabela, não a grade.
    const req: PedidoReprojecao = { id, ...pedido };
    w.postMessage(req);
  });
}

function canvasPrincipal(w: number, h: number) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D indisponível');
  return { canvas, ctx };
}

export const carregadorRadar: FrameLoader = {
  download: (url, signal) =>
    // Pedidos pequenos: 30 s de tempo-limite, e o cache repete (3 tentativas).
    fetchWithRetry(url, { mode: 'cors', credentials: 'omit' }, { retries: 0, signal, timeoutMs: 30_000, read: (r) => r.blob() }),
  decode: async (blob, larguraFinal = null, contexto) => {
    const radar = contexto ? radarDaChave(contexto.productId) : '';
    const produto = contexto?.productId.split(':')[1];
    const g = geometrias.get(radar);
    if (!g) throw new Error(`geometria do radar ${radar} desconhecida`);
    // Só a grade vai ao worker (a geometria completa tem o modelo, que não é clonável).
    const { extent: _extent, ...grade } = g.geo;
    const trocas = isProduto(produto) ? trocaDeCores(radar, produto) : null;
    const pedido = { blob, chave: g.chave, grade, larguraFinal, trocas };
    const viaWorker = noWorker(pedido);
    if (viaWorker) {
      try {
        return await viaWorker;
      } catch {
        // Falha no worker: tenta na thread principal (abaixo).
      }
    }
    return reprojetarBlob(blob, g.chave, grade, larguraFinal, canvasPrincipal, trocas);
  },
};

/** Largura "natural" do bitmap de um radar (a da grade reprojetada). */
export type LarguraRadar = number;

/**
 * Instância única. Downloads: 8 em paralelo (arquivos de ~15 KB, no mesmo
 * host HTTP/2); decodificações: 4 (dois workers, dois na fila de cada).
 */
export const radarCache = new FrameCache<LarguraRadar>(8, 4, carregadorRadar);
